import {IOfferTag} from "../types/IOfferTag";

interface IValueLabel {
    label: string;
    value: number;
}

interface IVariables {
    offer_facilities: IValueLabel[];
    offer_natural_sites: IValueLabel[];
    offer_standard_description_type: IValueLabel[];
}

interface IGroupedAmenities {
    standardAndSafety: IAmenities;
    utilities: IAmenities;
    recreation: IAmenities;
    nature: IAmenities;
    services: IAmenities;
}

export interface IAmenities {
    label: string;
    amenities: IValueLabel[];
}

enum FacilitiesStandardAndSafety {
    SECURITY = 2,
    MONITORING = 6,
    FENCED = 7,
    CONCIERGE = 8,
    RECEPTION = 16,
    ELEVATOR = 20,
    BIKE_PARKING = 22,
    STROLLER_ROOM = 28,
    BOAT_HARBOUR = 4,
    DISABLED_ACCESS = 15,
    SERVICES_IN_BUILDING = 19,
    PATIO_COURTYARD = 29,
    GUESTS_PARKING = 34
}

enum FacilitiesUtilities {
    AIR_CONDITIONING = 12,
    SMART_HOUSE = 23,
    PHOTOVOLTAICS = 30,
    CAR_CHARGER = 31,
    RECUPERATION = 32,
    HEAT_PUMP = 33,
    INTERNET = 11,
    CABLE_TV = 18,
    PRIVATE_WATER_SUPPLY = 21,
    VIDEO_INTERCOM = 24,
    OWN_BOILER_ROOM = 25
}

enum FacilitiesRecreation {
    OUTDOOR_GYM = 26,
    FITNESS_ROOM = 27,
    PLAYING_FIELD = 10,
    TENNIS = 14,
    POOL = 9,
    SAUNA = 17,
    PLAYGROUND = 3,
    WATER_BATH = 1,
    RECREATION_ROOM = 5,
    FITNESS_CLUB = 13
}

enum OfferTags {
    PARKS_NEARBY = 44
}

enum NaturalSiteFacilities {
    PARK = 1,
    FOREST = 2,
    LAKE = 3,
    RIVER = 4,
    POND = 5,
    SEA = 6,
    NATURE_RESERVE = 7,
    MOUNTAINS = 8,
    RESERVOIR = 9
}

enum ServicesFacilities {
    PARCEL_LOCKER = 35
}

const standardAndSafetyAmenitiesValue = [
    FacilitiesStandardAndSafety.MONITORING,
    FacilitiesStandardAndSafety.SECURITY,
    FacilitiesStandardAndSafety.FENCED,
    FacilitiesStandardAndSafety.RECEPTION,
    FacilitiesStandardAndSafety.BIKE_PARKING,
    FacilitiesStandardAndSafety.ELEVATOR,
    FacilitiesStandardAndSafety.CONCIERGE,
    FacilitiesStandardAndSafety.STROLLER_ROOM,
    FacilitiesStandardAndSafety.BOAT_HARBOUR,
    FacilitiesStandardAndSafety.DISABLED_ACCESS,
    FacilitiesStandardAndSafety.SERVICES_IN_BUILDING,
    FacilitiesStandardAndSafety.PATIO_COURTYARD,
    FacilitiesStandardAndSafety.GUESTS_PARKING
];

const utilitiesAmenitiesValue = [
    FacilitiesUtilities.PHOTOVOLTAICS,
    FacilitiesUtilities.RECUPERATION,
    FacilitiesUtilities.HEAT_PUMP,
    FacilitiesUtilities.AIR_CONDITIONING,
    FacilitiesUtilities.CAR_CHARGER,
    FacilitiesUtilities.SMART_HOUSE,
    FacilitiesUtilities.INTERNET,
    FacilitiesUtilities.CABLE_TV,
    FacilitiesUtilities.PRIVATE_WATER_SUPPLY,
    FacilitiesUtilities.VIDEO_INTERCOM,
    FacilitiesUtilities.OWN_BOILER_ROOM
];

const recreationAmenitiesValue = [
    FacilitiesRecreation.OUTDOOR_GYM,
    FacilitiesRecreation.FITNESS_ROOM,
    FacilitiesRecreation.PLAYING_FIELD,
    FacilitiesRecreation.TENNIS,
    FacilitiesRecreation.POOL,
    FacilitiesRecreation.SAUNA,
    FacilitiesRecreation.PLAYGROUND,
    FacilitiesRecreation.WATER_BATH,
    FacilitiesRecreation.RECREATION_ROOM,
    FacilitiesRecreation.FITNESS_CLUB
];

const natureAmenitiesValue = [
    NaturalSiteFacilities.FOREST,
    NaturalSiteFacilities.LAKE,
    NaturalSiteFacilities.PARK,
    NaturalSiteFacilities.SEA,
    NaturalSiteFacilities.RIVER,
    NaturalSiteFacilities.POND,
    NaturalSiteFacilities.NATURE_RESERVE,
    NaturalSiteFacilities.MOUNTAINS,
    NaturalSiteFacilities.RESERVOIR
];

const servicesAmenitiesValue = [ServicesFacilities.PARCEL_LOCKER];

export const getOfferAmenities = (
    offerFacilities: number[] | null,
    naturalSites: number[] | null,
    nearbyParksTag: IOfferTag[] | null,
    standardType: number[] | null,
    variables: IVariables | null
) => {
    const groupedAmenitiesObject: IGroupedAmenities = {
        standardAndSafety: {
            label: "Standard i bezpieczeństwo",
            amenities: []
        },
        utilities: {
            label: "Instalacje i energetyka",
            amenities: []
        },
        recreation: {
            label: "Rekreacja",
            amenities: []
        },
        nature: {
            label: "Przyroda w okolicy",
            amenities: []
        },
        services: {
            label: "Usługi na terenie osiedla",
            amenities: []
        }
    };

    const filterAndPushAmenity = (el: number, objectKey: keyof IGroupedAmenities, variableKey: keyof IVariables) => {
        if (!variables) {
            return;
        }

        const fullAmenityName = variables[variableKey].filter((facility) => facility.value === el);
        groupedAmenitiesObject[objectKey].amenities.push(...fullAmenityName);
    };

    if (offerFacilities && variables?.offer_facilities) {
        offerFacilities.forEach((el) => {
            if (standardAndSafetyAmenitiesValue.includes(el)) {
                filterAndPushAmenity(el, "standardAndSafety", "offer_facilities");
            } else if (utilitiesAmenitiesValue.includes(el)) {
                filterAndPushAmenity(el, "utilities", "offer_facilities");
            } else if (recreationAmenitiesValue.includes(el)) {
                filterAndPushAmenity(el, "recreation", "offer_facilities");
            } else if (servicesAmenitiesValue.includes(el)) {
                filterAndPushAmenity(el, "services", "offer_facilities");
            }
        });
    }

    // additional value for standardAndSafety which is not in offerFacilities
    if (standardType && variables?.offer_standard_description_type) {
        standardType.forEach((el) => {
            filterAndPushAmenity(el, "standardAndSafety", "offer_standard_description_type");
        });
    }

    //information about nearby parks
    if (nearbyParksTag && nearbyParksTag?.length > 0) {
        //Tags have different structure, at this moment it is necessary to normalize this
        if (nearbyParksTag[0].description && nearbyParksTag[0].id === OfferTags.PARKS_NEARBY) {
            const serializedParkInfo: IValueLabel = {
                label: nearbyParksTag[0].description,
                value: nearbyParksTag[0].id
            };

            groupedAmenitiesObject.nature.amenities.push(...[serializedParkInfo]);
        }
    }

    // natural places around investment
    if (naturalSites && variables?.offer_natural_sites) {
        naturalSites.forEach((el) => {
            if (natureAmenitiesValue.includes(el)) {
                filterAndPushAmenity(el, "nature", "offer_natural_sites");
            }
        });
    }

    // replace object to react friendly array
    let groupedAmenitiesArray: IAmenities[] = [];
    let key: keyof IGroupedAmenities;

    for (key in groupedAmenitiesObject) {
        if (groupedAmenitiesObject[key].amenities.length > 0) {
            groupedAmenitiesArray = [...groupedAmenitiesArray, groupedAmenitiesObject[key]];
        }
    }

    return groupedAmenitiesArray;
};
